// ---------------------------------------------------------------------------
// cropMode.js — advanced Canva-style crop MODE for the Design Editor (spec C).
//
// This module EXCLUSIVELY owns the "crop" mode (foundation enterMode/exitMode).
// It is wired in via features.js: `registerCropMode(editor)`.
//
// UX: double-click an image (or the Crop tool button / the `c` key) enters crop
// mode. The area outside the crop rectangle dims; the crop rectangle has 8 drag
// handles, a rule-of-thirds grid, aspect presets (Liber, 1:1, 4:3, 3:4, 16:9,
// 9:16) + a proportions lock. Apply commits the crop NON-DESTRUCTIVELY via fabric
// `cropX/cropY/width/height` (the source pixels stay intact); Cancel/Esc discards.
// One history snapshot is pushed on apply.
//
// -- WHY it works under the new viewport zoom/pan (and even rotation) ---------
// All crop geometry is expressed in SOURCE-image pixel space (units of cropX /
// cropY / width / height). The map source-pixel -> canvas/object space is:
//     S2O = M . T
//   where  T   = translate(-(cropX0 + width0/2), -(cropY0 + height0/2))
//                (fabric draws the visible region [cropX0..+width0] into the
//                 object-local rect [-width0/2..+width0/2], centred),
//          M   = img.calcTransformMatrix()  (object-local -> canvas, incl.
//                 the image's scale + angle + position).
// The crop rectangle is axis-aligned in SOURCE space (trivial to clamp/resize),
// while S2O carries the image's rotation/scale, so the very same code path draws
// and hit-tests correctly for rotated images. The final canvas->screen step is
// the viewport's `viewportTransform` (pure scale+translate, no rotation), so
// handles/borders drawn in object space stay crisp and screen-aligned at any
// zoom. We never mutate the image on enter (only on apply), so Cancel is a
// no-op restore.
//
// -- OWNERSHIP / SEAM USAGE ---------------------------------------------------
// Registers via the documented editor seam only:
//   editor.addToolButton(...)      — the Crop tool button
//   editor.registerCommand('c')    — enter/apply crop (foundation left 'c' free)
//   editor.registerCommand('enter')— apply while cropping
//   editor.addPanelSection(...)    — the crop controls (left properties column)
//   editor.enterMode('crop', { onExit }) / exitMode('crop')
// Canvas events it binds (only while cropping): mouse:down/move/up + after:render
// for the overlay, plus a lifetime mouse:dblclick to enter. It deliberately does
// NOT bind object:moving (guides agent owns it) and never attaches a window
// keydown (foundation owns keyboard dispatch).
// ---------------------------------------------------------------------------

import React, { useReducer, useEffect } from 'react';
import './cropMode.css';

const h = React.createElement;

const HANDLE_PX = 10;        // handle square size, screen px
const HANDLE_HIT_PX = 12;    // handle hit tolerance, screen px
const MIN_DISP = 16;         // minimum crop size, displayed px

const ASPECT_PRESETS = [
  { key: 'free', label: 'Liber', ratio: null },
  { key: '1x1', label: '1:1', ratio: 1 },
  { key: '4x3', label: '4:3', ratio: 4 / 3 },
  { key: '3x4', label: '3:4', ratio: 3 / 4 },
  { key: '16x9', label: '16:9', ratio: 16 / 9 },
  { key: '9x16', label: '9:16', ratio: 9 / 16 },
];

const CORNERS = ['tl', 'tr', 'br', 'bl'];
const ALL_HANDLES = ['tl', 'tm', 'tr', 'mr', 'br', 'bm', 'bl', 'ml'];

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const isCorner = (id) => CORNERS.indexOf(id) >= 0;
const lerp = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });

function cropIcon() {
  return h(
    'svg',
    { viewBox: '0 0 24 24', width: 18, height: 18 },
    h('path', {
      d: 'M7 17V1H5v4H1v2h4v10a2 2 0 0 0 2 2h10v4h2v-4h4v-2H7zm10-2h2V7a2 2 0 0 0-2-2H9v2h8v8z',
      fill: 'currentColor',
    }),
  );
}

// The crop controls panel. Holds no crop geometry itself — it just reflects and
// drives the crop controller, re-rendering on its own when the controller asks.
function CropPanel(props) {
  const ctl = props.ctl;
  const [, force] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    ctl.setPanelListener(force);
    return () => ctl.setPanelListener(null);
  }, [ctl]);

  const activeRatio = ctl.getAspect();
  const locked = ctl.isLocked();

  const presetBtns = ASPECT_PRESETS.map((p) => {
    const active = p.ratio === null
      ? activeRatio === null
      : (activeRatio !== null && Math.abs(activeRatio - p.ratio) < 1e-3);
    return h('button', {
      key: p.key,
      type: 'button',
      className: 'de-btn-sm de-crop-preset' + (active ? ' active' : ''),
      onClick: () => ctl.setAspect(p.ratio),
    }, p.label);
  });

  return h('div', { className: 'de-crop-panel' },
    h('div', { className: 'de-crop-presets' }, presetBtns),
    h('div', { className: 'de-row', style: { marginTop: 8 } },
      h('button', {
        type: 'button',
        title: 'Blocheaza proportiile',
        className: 'de-btn-sm' + (locked ? ' active' : ''),
        style: { width: 'auto', flex: 1 },
        onClick: () => ctl.toggleLock(),
      }, locked ? 'Proportii blocate' : 'Proportii libere')),
    h('div', { className: 'de-crop-actions' },
      h('button', { type: 'button', className: 'de-btn de-btn-primary', onClick: () => ctl.apply() }, 'Aplica'),
      h('button', { type: 'button', className: 'de-btn de-crop-cancel', onClick: () => ctl.cancel() }, 'Renunta')),
    h('div', { className: 'de-crop-hint' }, 'Trage de colturi sau margini ca sa decupezi. Enter aplica, Esc renunta.'),
  );
}

export function registerCropMode(editor) {
  const canvas = editor.canvas;
  const fabric = editor.fabric;

  // -- per-session crop state (reset on every enter) -------------------------
  let cropActive = false;
  let target = null;          // the fabric.Image being cropped
  let srcEl = null;           // its source element (what fabric draws)
  let elW = 0;
  let elH = 0;                // source dimensions, px
  let scaleX = 1;
  let scaleY = 1;             // the image's on-canvas scale
  let S2O = null;             // source-pixel -> object/canvas matrix
  let invS2O = null;          // its inverse
  let frame = { x: 0, y: 0, w: 0, h: 0 };  // crop rect, source px (axis-aligned)
  let aspect = null;          // target DISPLAY ratio (w/h) or null = Liber
  let locked = false;
  let drag = null;            // { mode, startSrc, startFrame }
  let prevSelection = true;
  let panelRemove = null;
  let panelListener = null;

  const notifyPanel = () => { if (panelListener) panelListener(); };

  // -- coordinate helpers ----------------------------------------------------
  const srcToObj = (p) => fabric.util.transformPoint({ x: p.x, y: p.y }, S2O);
  const objToSrc = (p) => fabric.util.transformPoint({ x: p.x, y: p.y }, invS2O);
  // Scene (object-space) pointer for a fabric mouse event, robust to zoom/pan.
  const scenePointer = (opt) => (opt && opt.absolutePointer)
    ? opt.absolutePointer
    : canvas.getPointer(opt.e);

  function computeGeometry(img) {
    srcEl = img._element || img._originalElement;
    if (!srcEl) { elW = 0; elH = 0; return; }
    elW = srcEl.naturalWidth || srcEl.width || 0;
    elH = srcEl.naturalHeight || srcEl.height || 0;
    const cropX0 = img.cropX || 0;
    const cropY0 = img.cropY || 0;
    const w0 = img.width;
    const h0 = img.height;
    scaleX = img.scaleX || 1;
    scaleY = img.scaleY || 1;
    const T = [1, 0, 0, 1, -(cropX0 + w0 / 2), -(cropY0 + h0 / 2)];
    const M = img.calcTransformMatrix();
    S2O = fabric.util.multiplyTransformMatrices(M, T);
    invS2O = fabric.util.invertTransform(S2O);
    // Initial crop rect = the image's current visible region.
    frame = { x: cropX0, y: cropY0, w: w0, h: h0 };
  }

  function handleSrcPoint(id) {
    const x = frame.x;
    const y = frame.y;
    const w = frame.w;
    const hh = frame.h;
    switch (id) {
      case 'tl': return { x: x, y: y };
      case 'tm': return { x: x + w / 2, y: y };
      case 'tr': return { x: x + w, y: y };
      case 'mr': return { x: x + w, y: y + hh / 2 };
      case 'br': return { x: x + w, y: y + hh };
      case 'bm': return { x: x + w / 2, y: y + hh };
      case 'bl': return { x: x, y: y + hh };
      case 'ml': return { x: x, y: y + hh / 2 };
      default: return { x: x, y: y };
    }
  }

  // -- geometry math ---------------------------------------------------------
  function clampFrameToBounds() {
    let x = frame.x;
    let y = frame.y;
    let w = frame.w;
    let hh = frame.h;
    if (w > elW) w = elW;
    if (hh > elH) hh = elH;
    if (x < 0) x = 0;
    if (y < 0) y = 0;
    if (x + w > elW) x = elW - w;
    if (y + hh > elH) y = elH - hh;
    frame = { x: x, y: y, w: w, h: hh };
  }

  function moveFrame(sf, dx, dy) {
    frame = { x: sf.x + dx, y: sf.y + dy, w: sf.w, h: sf.h };
    clampFrameToBounds();
  }

  function applyAspectToRect(handle, nf) {
    const srcRatio = aspect * (scaleY / scaleX); // w/h in source px
    let ax;
    let ay;
    let dirX;
    let dirY;
    if (handle === 'br') { ax = nf.x; ay = nf.y; dirX = 1; dirY = 1; }
    else if (handle === 'tr') { ax = nf.x; ay = nf.y + nf.h; dirX = 1; dirY = -1; }
    else if (handle === 'bl') { ax = nf.x + nf.w; ay = nf.y; dirX = -1; dirY = 1; }
    else { ax = nf.x + nf.w; ay = nf.y + nf.h; dirX = -1; dirY = -1; } // tl
    let w = nf.w;
    let hh = nf.h;
    if (w / srcRatio >= hh) hh = w / srcRatio; else w = hh * srcRatio;
    const maxW = dirX > 0 ? (elW - ax) : ax;
    const maxH = dirY > 0 ? (elH - ay) : ay;
    let s = 1;
    if (w > maxW && w > 0) s = Math.min(s, maxW / w);
    if (hh > maxH && hh > 0) s = Math.min(s, maxH / hh);
    w *= s;
    hh *= s;
    const minW = Math.max(1, MIN_DISP / scaleX);
    const minH = Math.max(1, MIN_DISP / scaleY);
    if (w < minW) { w = minW; hh = w / srcRatio; }
    if (hh < minH) { hh = minH; w = hh * srcRatio; }
    const x = dirX > 0 ? ax : ax - w;
    const y = dirY > 0 ? ay : ay - hh;
    return { x: x, y: y, w: w, h: hh };
  }

  function resizeFromHandle(handle, sf, dx, dy) {
    const minW = Math.min(Math.max(1, MIN_DISP / scaleX), elW);
    const minH = Math.min(Math.max(1, MIN_DISP / scaleY), elH);
    let L = sf.x;
    let T = sf.y;
    let R = sf.x + sf.w;
    let B = sf.y + sf.h;
    const movesL = handle === 'tl' || handle === 'bl' || handle === 'ml';
    const movesR = handle === 'tr' || handle === 'br' || handle === 'mr';
    const movesT = handle === 'tl' || handle === 'tr' || handle === 'tm';
    const movesB = handle === 'bl' || handle === 'br' || handle === 'bm';
    if (movesL) L = clamp(sf.x + dx, 0, R - minW);
    if (movesR) R = clamp(sf.x + sf.w + dx, L + minW, elW);
    if (movesT) T = clamp(sf.y + dy, 0, B - minH);
    if (movesB) B = clamp(sf.y + sf.h + dy, T + minH, elH);
    let nf = { x: L, y: T, w: R - L, h: B - T };
    if (locked && aspect !== null && isCorner(handle)) nf = applyAspectToRect(handle, nf);
    frame = nf;
  }

  function reshapeToAspect(ratio) {
    const srcRatio = ratio * (scaleY / scaleX);
    const cx = frame.x + frame.w / 2;
    const cy = frame.y + frame.h / 2;
    let hh = frame.h;
    let w = hh * srcRatio;
    if (w > frame.w) { w = frame.w; hh = w / srcRatio; }
    if (w > elW) { const s = elW / w; w *= s; hh *= s; }
    if (hh > elH) { const s = elH / hh; w *= s; hh *= s; }
    let x = cx - w / 2;
    let y = cy - hh / 2;
    if (x < 0) x = 0;
    if (y < 0) y = 0;
    if (x + w > elW) x = elW - w;
    if (y + hh > elH) y = elH - hh;
    frame = { x: x, y: y, w: w, h: hh };
  }

  // -- hit testing + drag ----------------------------------------------------
  function hitTest(opt) {
    const pObj = scenePointer(opt);
    const zoom = canvas.getZoom() || 1;
    const tol = HANDLE_HIT_PX / zoom;
    const ids = locked ? CORNERS : ALL_HANDLES;
    for (let i = 0; i < ids.length; i++) {
      const op = srcToObj(handleSrcPoint(ids[i]));
      if (Math.abs(op.x - pObj.x) <= tol && Math.abs(op.y - pObj.y) <= tol) return ids[i];
    }
    const sp = objToSrc(pObj);
    if (sp.x >= frame.x && sp.x <= frame.x + frame.w && sp.y >= frame.y && sp.y <= frame.y + frame.h) return 'move';
    return null;
  }

  function onDown(opt) {
    if (!cropActive) return;
    // Keep marquee-selection off for the whole session (the viewport's pan
    // mouse-up re-enables canvas.selection, which could otherwise start a stray
    // rubber-band on an empty left-drag while cropping).
    canvas.selection = false;
    const e = opt.e;
    if (e && e.button !== undefined && e.button !== 0) return; // left button only
    const hit = hitTest(opt);
    if (!hit) return;
    drag = { mode: hit, startSrc: objToSrc(scenePointer(opt)), startFrame: { x: frame.x, y: frame.y, w: frame.w, h: frame.h } };
    if (e && e.preventDefault) e.preventDefault();
  }

  function onMove(opt) {
    if (!cropActive || !drag) return;
    const cur = objToSrc(scenePointer(opt));
    const dx = cur.x - drag.startSrc.x;
    const dy = cur.y - drag.startSrc.y;
    if (drag.mode === 'move') moveFrame(drag.startFrame, dx, dy);
    else resizeFromHandle(drag.mode, drag.startFrame, dx, dy);
    canvas.requestRenderAll();
  }

  function onUp() {
    if (drag) drag = null;
  }

  // -- overlay: faded source + dim + frame + handles -------------------------
  function overlayRender(opt) {
    if (!cropActive || !S2O) return;
    if (editor.isExporting && editor.isExporting()) return; // never bake crop UI into an export
    const ctx = opt.ctx;
    if (!ctx) return;
    const vpt = canvas.viewportTransform;
    const zoom = vpt[0] || 1;

    // Pass 1 (image-local, rotates with the image): draw the FULL source so the
    // user can expand the crop back out, then dim everything outside the frame.
    ctx.save();
    ctx.transform(vpt[0], vpt[1], vpt[2], vpt[3], vpt[4], vpt[5]);
    ctx.transform(S2O[0], S2O[1], S2O[2], S2O[3], S2O[4], S2O[5]);
    if (srcEl) {
      ctx.globalAlpha = (target && target.opacity != null) ? target.opacity : 1;
      try { ctx.drawImage(srcEl, 0, 0, elW, elH); } catch (e) { /* tainted src */ }
      ctx.globalAlpha = 1;
    }
    ctx.beginPath();
    ctx.rect(0, 0, elW, elH);
    ctx.rect(frame.x, frame.y, frame.w, frame.h);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fill('evenodd');
    ctx.restore();

    // Pass 2 (object space; the viewport has no rotation, so handles stay
    // screen-aligned and lines stay 1px regardless of zoom).
    const c = {
      tl: srcToObj({ x: frame.x, y: frame.y }),
      tr: srcToObj({ x: frame.x + frame.w, y: frame.y }),
      br: srcToObj({ x: frame.x + frame.w, y: frame.y + frame.h }),
      bl: srcToObj({ x: frame.x, y: frame.y + frame.h }),
    };
    ctx.save();
    ctx.transform(vpt[0], vpt[1], vpt[2], vpt[3], vpt[4], vpt[5]);
    // frame border
    ctx.lineWidth = 1.5 / zoom;
    ctx.strokeStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(c.tl.x, c.tl.y);
    ctx.lineTo(c.tr.x, c.tr.y);
    ctx.lineTo(c.br.x, c.br.y);
    ctx.lineTo(c.bl.x, c.bl.y);
    ctx.closePath();
    ctx.stroke();
    // rule-of-thirds grid
    ctx.lineWidth = 1 / zoom;
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.beginPath();
    for (let i = 1; i <= 2; i++) {
      const t = i / 3;
      const a = lerp(c.tl, c.tr, t);
      const b = lerp(c.bl, c.br, t);
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      const a2 = lerp(c.tl, c.bl, t);
      const b2 = lerp(c.tr, c.br, t);
      ctx.moveTo(a2.x, a2.y);
      ctx.lineTo(b2.x, b2.y);
    }
    ctx.stroke();
    // handles
    const hs = HANDLE_PX / zoom;
    const ids = locked ? CORNERS : ALL_HANDLES;
    ctx.lineWidth = 1 / zoom;
    ids.forEach((id) => {
      const p = srcToObj(handleSrcPoint(id));
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#4a6fa5';
      ctx.fillRect(p.x - hs / 2, p.y - hs / 2, hs, hs);
      ctx.strokeRect(p.x - hs / 2, p.y - hs / 2, hs, hs);
    });
    ctx.restore();
  }

  // -- mode lifecycle --------------------------------------------------------
  function enterCrop(img) {
    if (cropActive) return;
    if (!img || img.type !== 'image') {
      editor.toast('Selecteaza o imagine ca sa o decupezi.', 'info');
      return;
    }
    computeGeometry(img);
    if (!elW || !elH || !frame.w || !frame.h) {
      editor.toast('Imaginea nu poate fi decupata.', 'error');
      return;
    }
    target = img;
    aspect = null;
    locked = false;
    drag = null;
    // enterMode first so any other active mode tears down before we lock the
    // canvas (foundation auto-exits the previous mode here).
    editor.enterMode('crop', { onExit: teardown });
    cropActive = true;
    prevSelection = canvas.selection;
    canvas.selection = false;
    canvas.discardActiveObject();
    canvas.getObjects().forEach((o) => {
      o._cropPrevSelectable = o.selectable;
      o._cropPrevEvented = o.evented;
      o.selectable = false;
      o.evented = false;
    });
    canvas.on('mouse:down', onDown);
    canvas.on('mouse:move', onMove);
    canvas.on('mouse:up', onUp);
    canvas.on('after:render', overlayRender);
    panelRemove = editor.addPanelSection({
      id: 'crop-mode',
      title: 'Decupare',
      order: -100,
      when: () => cropActive,
      render: () => h(CropPanel, { ctl: controller }),
    });
    canvas.requestRenderAll();
  }

  // Runs for BOTH apply and cancel/Esc (foundation exitMode -> onExit). The image
  // is only ever mutated in applyCrop (before exitMode), so cancel needs no
  // restore — we just unwind the UI and re-enable interaction.
  function teardown() {
    if (!cropActive) return;
    cropActive = false;
    canvas.off('mouse:down', onDown);
    canvas.off('mouse:move', onMove);
    canvas.off('mouse:up', onUp);
    canvas.off('after:render', overlayRender);
    if (panelRemove) { panelRemove(); panelRemove = null; }
    canvas.getObjects().forEach((o) => {
      if (o._cropPrevSelectable !== undefined) {
        o.selectable = o._cropPrevSelectable;
        o.evented = o._cropPrevEvented;
        delete o._cropPrevSelectable;
        delete o._cropPrevEvented;
      }
    });
    canvas.selection = prevSelection;
    if (target && canvas.getObjects().indexOf(target) >= 0) {
      canvas.setActiveObject(target);
    }
    drag = null;
    target = null;
    S2O = null;
    invS2O = null;
    srcEl = null;
    canvas.requestRenderAll();
  }

  function applyCrop() {
    if (!cropActive || !target || !S2O) return;
    const img = target; // capture before teardown nulls it
    const cx = frame.x + frame.w / 2;
    const cy = frame.y + frame.h / 2;
    const objCenter = srcToObj({ x: cx, y: cy });
    img.set({ cropX: frame.x, cropY: frame.y, width: frame.w, height: frame.h });
    img.setPositionByOrigin(new fabric.Point(objCenter.x, objCenter.y), 'center', 'center');
    img.setCoords();
    img.dirty = true;
    editor.exitMode('crop'); // triggers teardown (re-enables interaction)
    canvas.setActiveObject(img);
    editor.pushHistory('Decupare');
    if (editor.refreshLayers) editor.refreshLayers();
    canvas.requestRenderAll();
  }

  function setAspect(ratio) {
    aspect = ratio;
    locked = ratio !== null;
    if (ratio !== null) reshapeToAspect(ratio);
    notifyPanel();
    canvas.requestRenderAll();
  }

  function toggleLock() {
    locked = !locked;
    if (locked) {
      if (aspect === null) aspect = (frame.w * scaleX) / (frame.h * scaleY);
      reshapeToAspect(aspect);
      canvas.requestRenderAll();
    }
    notifyPanel();
  }

  const isImageActive = () => {
    const o = canvas.getActiveObject();
    return !!(o && o.type === 'image');
  };

  const controller = {
    getAspect: () => aspect,
    isLocked: () => locked,
    setAspect: setAspect,
    toggleLock: toggleLock,
    apply: applyCrop,
    cancel: () => { if (cropActive) editor.exitMode('crop'); },
    setPanelListener: (fn) => { panelListener = fn; },
  };

  // -- registrations (once, at feature-wiring time) --------------------------
  editor.addToolButton({
    id: 'crop',
    title: 'Decupeaza (C)',
    icon: cropIcon(),
    isActive: () => cropActive,
    onClick: () => {
      if (cropActive) applyCrop();
      else enterCrop(canvas.getActiveObject());
    },
  });

  editor.registerCommand('c', () => {
    if (cropActive) applyCrop();
    else enterCrop(canvas.getActiveObject());
  }, { when: () => cropActive || isImageActive() });

  editor.registerCommand('enter', () => {
    if (cropActive) applyCrop();
  }, { when: () => cropActive });

  // Double-click an image to start cropping.
  canvas.on('mouse:dblclick', (opt) => {
    if (cropActive) return;
    if (editor.getMode && editor.getMode()) return; // another mode owns the canvas
    const t = opt.target;
    if (t && t.type === 'image' && t.selectable !== false) enterCrop(t);
  });
}
